import { format } from "date-fns";
import { Text } from "../../components";
import { OrderStatusChip } from "./orderStatusChip";
import type { TableColumn } from "../../components/Table";
import type { OrderItem, OrderItemUnifiedStatus } from "../../apis/order";
import { OrderStatus } from "../../apis/order";
import { Tools } from "../../utils";

function AppointmentDateCell({ row }: { row: OrderItem }) {
  const timeZone = row.partnerLocation?.timeZone;
  if (!row.serviceDateTime) return <Text>{"--"}</Text>;

  function formatDate(iso: string) {
    const date = Tools.zonedDate(iso, timeZone);
    if (!date) return iso;
    return `${format(date, "dd/MM/yyyy, HH:mm")} (${timeZone})`;
  }

  const current = formatDate(row.serviceDateTime);
  const originalDateTime = row.originalBooking?.serviceDateTime;

  if (originalDateTime)
    return (
      <div className="grid gap-[2px]">
        <Text>{current}</Text>
        <Text className="line-through text-critical">
          {formatDate(originalDateTime)}
        </Text>
      </div>
    );
  if (row.order?.orderStatus === OrderStatus.cancelled)
    return <Text className="line-through text-critical">{current}</Text>;
  return <Text>{current}</Text>;
}

export function getManageBookingsColumns({
  t,
}: {
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  return [
    {
      key: "serviceDateTime",
      title: t("manageBookings.columns.date"),
      className: "min-w-[300px]",
      classNames: { cell: "h-[70px]" },
      sorter: true,
      render: (_: string, row: OrderItem) => <AppointmentDateCell row={row} />,
    },
    {
      key: "order.code",
      title: t("manageBookings.columns.orderNumber"),
      className: "min-w-[160px]",
    },
    {
      key: "visitorFirstName,visitorLastName",
      title: t("general.name"),
      className: "min-w-[180px]",
      render: (value: (string | undefined)[]) =>
        value.filter(Boolean).join(" ") || "--",
    },
    {
      key: "productName",
      title: t("manageBookings.columns.service"),
      className: "min-w-[180px]",
    },
    {
      key: "partnerLocation.code",
      title: t("manageBookings.columns.location"),
      className: "min-w-[160px]",
    },
    {
      key: "insurancePolicyNumber",
      title: t("manageBookings.columns.policyCaseNumber"),
      className: "min-w-[160px]",
    },
    {
      key: "insuranceProviderName",
      title: t("manageBookings.columns.insuranceProvider"),
      className: "min-w-[160px]",
    },
    {
      key: "unifiedStatus",
      title: t("manageBookings.columns.status"),
      className: "min-w-[120px]",
      sorter: true,
      render: (unifiedStatus: OrderItemUnifiedStatus) => (
        <OrderStatusChip status={unifiedStatus} />
      ),
    },
  ] as TableColumn<OrderItem>[];
}
